<template>
    <div class="p-3">
        <back-to-settings />

        <div v-if="state === 'loading'" class="text-center py-5">
            <generic-loading />
        </div>

        <div v-else-if="state === 'confirm'">
            <div class="text-center py-3">
                <div class="text-primary mb-3">
                    <font-awesome-icon
                        icon="fa-duotone fa-shield-check"
                        size="4x"
                    />
                </div>
                <h4>Update Verification Video</h4>
                <p class="text-secondary">
                    Record a new verification video to replace your current one.
                    Your face must be clearly visible and you'll need to say a
                    verification code aloud.
                </p>
                <p>
                    This will cost
                    <coin-badge :amount="prices.userVideoUpdate" />
                </p>
            </div>
            <b-btn
                @click="startUpdate"
                :disabled="submitting"
                pill
                variant="primary"
                size="lg"
                block
            >
                <b-spinner small v-if="submitting" />
                <span v-else>
                    <font-awesome-icon icon="fa-duotone fa-camcorder" />
                    Update My Video
                </span>
            </b-btn>
        </div>

        <div v-else-if="state === 'recording'">
            <verification-prompt :code="code" />

            <verification-record
                class="my-3"
                @uploaded="onUploaded"
            />

            <div v-if="!recording && !recordingUploading" class="mt-3">
                <div v-if="submitting" class="text-center py-3">
                    <generic-loading label="Submitting" />
                </div>
                <verification-upload v-else @uploaded="onUploaded" />
            </div>
        </div>

        <div v-else-if="state === 'submitted'">
            <div class="text-center">
                <div class="text-success mb-3">
                    <font-awesome-icon
                        icon="fa-duotone fa-circle-check"
                        size="4x"
                    />
                </div>
                <h4>Video Submitted</h4>
                <p class="text-secondary">
                    Your video has been submitted and is pending review.
                </p>
            </div>

            <div v-if="pendingVideos?.length">
                <b-alert variant="primary" show>
                    Preview your verification video. If the code doesn't match,
                    you can delete the video and try again
                </b-alert>

                <div
                    :key="video.id"
                    v-for="video in pendingVideos"
                    class="bg-white p-3"
                >
                    <div>Verification Code: {{ video.code }}</div>
                    <video-player
                        :loop="false"
                        :muted="false"
                        :controls="true"
                        :autoplay="false"
                        :mp4Url="video.mp4Url"
                        :webmUrl="video.webmUrl"
                    />
                    <b-btn
                        variant="light"
                        @click="deleteVideo(video)"
                        class="text-danger mt-2"
                        pill
                        block
                    >
                        <font-awesome-icon
                            icon="fa-light fa-times"
                            class="mr-1"
                        />
                        Delete Video
                    </b-btn>
                </div>
            </div>
            <div v-else-if="failedVideos?.length">
                <div
                    :key="video.id"
                    v-for="video in failedVideos"
                    class="bg-white p-3"
                >
                    <div class="text-danger">
                        Video failed to process, please delete it and submit a
                        new video
                    </div>
                    <b-alert
                        v-if="video.rejectReason"
                        variant="danger"
                        show
                        class="my-3"
                    >
                        <h5>Reason</h5>
                        <font-awesome-icon
                            icon="fa-duotone fa-triangle-exclamation"
                        />
                        {{ video.rejectReason }}
                    </b-alert>
                    <b-btn
                        variant="light"
                        @click="deleteVideo(video)"
                        class="text-danger"
                        pill
                        block
                    >
                        <font-awesome-icon
                            icon="fa-light fa-times"
                            class="mr-1"
                        />
                        Delete Video
                    </b-btn>
                </div>
            </div>
            <div v-else>
                <generic-loading label="Processing Video" />

                <div class="mt-3 text-center">
                    <b-btn
                        v-if="allowManualRefresh"
                        @click="refreshPending"
                        variant="primary"
                        pill
                    >
                        Refresh
                    </b-btn>
                </div>
            </div>
        </div>

        <div v-else-if="state === 'insufficientCredits'" class="text-center py-3">
            <div class="text-warning mb-3">
                <font-awesome-icon
                    icon="fa-duotone fa-coins"
                    size="4x"
                />
            </div>
            <h4>Insufficient Credits</h4>
            <p class="text-secondary">
                You need
                <coin-badge :amount="prices.userVideoUpdate" />
                to update your verification video.
            </p>
        </div>
    </div>
</template>

<script>
import { mapState } from "vuex";

import BackToSettings from "@/components/setting/utility/BackToSettings";
import VideoPlayer from "@/components/utility/VideoPlayer";
import VerificationPrompt from "@/components/verification/VerificationPrompt";
import VerificationRecord from "@/components/verification/VerificationRecord";
import VerificationUpload from "@/components/verification/VerificationUpload";

export default {
    mounted() {
        this.checkStatus();
    },
    data() {
        return {
            state: "loading",
            code: null,
            submitting: false,
            pendingVideos: null,
            failedVideos: null,
            allowManualRefresh: false,
            timeouts: [],
        };
    },
    computed: {
        ...mapState("options", ["prices"]),
        ...mapState("verification", ["recording", "recordingUploading"]),
    },
    methods: {
        checkStatus() {
            this.state = "loading";

            this.api.post("/userVideo/updateStatus").then((response) => {
                if (response?.data?.active) {
                    this.code = response.data.code;
                    this.checkForPendingVideos();
                } else {
                    this.state = "confirm";
                }
            });
        },
        checkForPendingVideos() {
            this.api.post("/userVideo/pending").then((response) => {
                const hasVideos = response?.data?.videos?.length
                    || response?.data?.failed?.length;

                if (hasVideos) {
                    this.pendingVideos = response.data.videos;
                    this.failedVideos = response.data.failed;
                    this.state = "submitted";
                } else {
                    this.state = "recording";
                }
            });
        },
        startUpdate() {
            this.submitting = true;

            this.api
                .post("/userVideo/startUpdate")
                .then((response) => {
                    this.code = response?.data?.code;
                    this.state = "recording";
                })
                .catch((error) => {
                    if (error?.response?.status === 422) {
                        this.state = "insufficientCredits";
                    }
                })
                .finally(() => {
                    this.submitting = false;
                });
        },
        onUploaded(url) {
            this.submitting = true;

            this.api
                .post("/userVideo/uploadUpdate", {
                    url,
                    code: this.code,
                })
                .then(() => {
                    this.state = "submitted";
                    this.startPolling();
                })
                .finally(() => {
                    this.submitting = false;
                });
        },
        startPolling() {
            this.pendingVideos = null;
            this.failedVideos = null;
            this.allowManualRefresh = false;

            this.refreshPending();

            const giveUpAfter = 120000;

            this.schedule(5000);
            this.schedule(10000);
            this.schedule(15000);
            this.schedule(30000);
            this.schedule(45000);
            this.schedule(giveUpAfter);

            this.timeouts.push(
                setTimeout(() => {
                    this.allowManualRefresh = true;
                }, giveUpAfter + 3000)
            );
        },
        schedule(delay) {
            this.timeouts.push(
                setTimeout(() => {
                    if (this.pendingVideos?.length) {
                        return;
                    }

                    this.refreshPending();
                }, delay)
            );
        },
        refreshPending() {
            this.api.post("/userVideo/pending").then((response) => {
                this.pendingVideos = response?.data?.videos;
                this.failedVideos = response?.data?.failed;
            });
        },
        deleteVideo(video) {
            this.api.post(`/userVideo/${video.id}/delete`).then(() => {
                this.pendingVideos = null;
                this.failedVideos = null;
                this.clearTimeouts();
                this.state = "recording";
            });
        },
        clearTimeouts() {
            this.timeouts.forEach((timeout) => clearTimeout(timeout));
            this.timeouts = [];
        },
    },
    beforeDestroy() {
        this.clearTimeouts();
    },
    components: {
        BackToSettings,
        VideoPlayer,
        VerificationPrompt,
        VerificationRecord,
        VerificationUpload,
    },
};
</script>
